import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, Skeleton, EmptyState } from "@/hq/HQPrimitives";
import ActivityTimeline from "@/hq/ActivityTimeline";
import ClientFormModal from "@/hq/clients/ClientFormModal";
import ClientEmailModal from "@/hq/clients/ClientEmailModal";
import { DeleteClientModal } from "@/hq/clients/ClientDeleteModals";
import ClientServicesTab from "@/hq/clients/ClientServicesTab";
import ClientSubscriptionsTab from "@/hq/clients/ClientSubscriptionsTab";
import ClientInvoicesTab from "@/hq/clients/ClientInvoicesTab";
import ClientOrangeFixTab from "@/hq/clients/ClientOrangeFixTab";
import ClientCommsTimeline from "@/hq/clients/ClientCommsTimeline";
import ClientActivityTimeline from "@/hq/clients/ClientActivityTimeline";
import ClientPitchHistory from "@/hq/clients/ClientPitchHistory";
import OrangeScoreBadge, { classifyScore, CLASSIFICATION_LABEL } from "@/hq/OrangeScoreBadge";
import { TERRITORY_META, territoryFlag, territoryLabel } from "@/hq/territory";
import NudgeChip from "@/hq/clients/NudgeChip";
import SalesPitchModal from "@/hq/clients/SalesPitchModal";
import ClientPitchTab from "@/hq/clients/ClientPitchTab";
import ClientAgreementsTab from "@/hq/clients/ClientAgreementsTab";
import PhoneDisplay from "@/components/site/PhoneDisplay";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Send, ExternalLink, RefreshCw,
  Pencil, Trash2,
} from "lucide-react";

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYM[cur] || cur + " "}${(Number(minor || 0) / 100).toFixed(2)}`;

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "services", label: "Services" },
  { key: "pitch",    label: "Pitch" },
  { key: "subs",     label: "Subscriptions" },
  { key: "invoices", label: "Invoices" },
  { key: "agreements", label: "Agreements" },
  { key: "fix",      label: "Orange Fix" },
  { key: "comms",    label: "Communications" },
  { key: "activity", label: "Activity" },
];

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#EAEAEA] text-sm">
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className="col-span-2 break-words">{value ?? "—"}</div>
    </div>
  );
}

function ActionButton({ children, onClick, primary, testId, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] font-black tracking-[0.16em] uppercase transition-colors ${
        primary
          ? "bg-[#FF6A00] text-black hover:bg-[#e75f00] disabled:opacity-50"
          : "bg-white border border-[#EAEAEA] hover:border-[#FF6A00] disabled:opacity-50"
      }`}
    >
      {children}
    </button>
  );
}

export default function HQClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pitchType, setPitchType] = useState(null); // "website" | "optimise" | "crm" | "full" | null
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    hqApi.get(`/control/clients/${id}`)
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        // Phase 3G.10 — auto-open Pitch tab for fresh prospects that have
        // never been pitched yet. Only nudge when there's real pitch
        // content to see (an Orange Score exists), never override a user
        // who's already navigated to another tab in this session.
        if (r.data?.has_any_pitch_sent === false
            && r.data?.orange_score_current != null) {
          setTab((prev) => (prev === "overview" ? "pitch" : prev));
        }
      })
      .catch(() => alive && toast.error("Could not load this client."));
    return () => { alive = false; };
  }, [id]);

  if (!data) return (
    <div className="space-y-4">
      <Skeleton h={40} w={200}/>
      <Card className="p-6 space-y-3"><Skeleton h={20}/><Skeleton h={20}/><Skeleton h={20}/></Card>
    </div>
  );

  const b = data.business || {};
  const contact = data.contacts?.[0] || {};
  const web = data.websites?.[0];

  const refresh = () => hqApi.get(`/control/clients/${id}`).then((r2) => setData(r2.data));

  const doScoreRescan = async () => {
    if (rescanning) return;
    setRescanning(true);
    try {
      const r = await hqApi.post(`/control/clients/${id}/score-rescan`);
      const { current_score, previous_score, delta } = r.data;
      if (previous_score != null && delta != null) {
        const dir = delta > 0 ? `↑ +${delta}` : delta < 0 ? `↓ ${delta}` : "no change";
        toast.success(`Orange Score ${current_score} (${dir})`);
      } else {
        toast.success(`Orange Score ${current_score}`);
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Rescan failed");
    } finally {
      setRescanning(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="hq-client-profile">
      <Link to="/hq/clients" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A] hover:text-[#0A0A0A]" data-testid="hq-client-back">
        <ArrowLeft size={14}/> All clients
      </Link>

      <Card className="p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-start gap-6">
            <div className="flex flex-col items-center" data-testid="hq-client-score-hero">
              <OrangeScoreBadge
                score={data.orange_score_current}
                size="lg"
                testId="hq-client-orange-score-badge"
              />
              <div className="mt-2 text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">
                Orange Score
              </div>
              <div className="text-[11px] tracking-[0.22em] uppercase font-black text-[#0A0A0A]"
                   data-testid="hq-client-orange-classification">
                {CLASSIFICATION_LABEL[classifyScore(data.orange_score_current)]}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Client</div>
              <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]" data-testid="hq-client-name">{b.name || "—"}</h1>
              <div className="mt-2 text-[13px] text-[#4A4A4A]">
                {b.industry || "—"} · {b.status || "prospect"} · {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
              </div>
              {data.orange_score_current != null && (
                <span className="hidden" data-testid="hq-client-orange-score">{data.orange_score_current}</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionButton onClick={doScoreRescan} disabled={rescanning} testId="hq-client-rescan-score">
              <RefreshCw size={14} className={rescanning ? "animate-spin" : ""}/> {rescanning ? "Rescanning…" : "Rescan score"}
            </ActionButton>
            <ActionButton onClick={() => setEditOpen(true)} testId="hq-client-edit">
              <Pencil size={14}/> Edit client
            </ActionButton>
            <ActionButton primary onClick={() => setIntroOpen(true)} testId="hq-client-send-intro">
              <Mail size={14}/> Send introduction
            </ActionButton>
            <ActionButton onClick={() => setPreviewOpen(true)} testId="hq-client-send-preview">
              <Send size={14}/> Send website preview
            </ActionButton>
            <ActionButton onClick={() => setDeleteOpen(true)} testId="hq-client-delete">
              <Trash2 size={14}/> Delete / archive
            </ActionButton>
          </div>
        </div>
      </Card>

      <ClientFormModal
        open={editOpen}
        initial={data ? { ...data, id: data.business?.id } : null}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); hqApi.get(`/control/clients/${id}`).then((r) => setData(r.data)); }}
      />
      <DeleteClientModal
        open={deleteOpen}
        businessId={id}
        businessName={b.name}
        onClose={() => setDeleteOpen(false)}
        onDone={() => { setDeleteOpen(false); navigate("/hq/clients"); }}
      />

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto" data-testid="hq-client-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`hq-client-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase whitespace-nowrap ${
              tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] hover:bg-[#F6F5F2]"
            }`}
            dangerouslySetInnerHTML={{ __html: t.label }}
          />
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {data.next_best_action && (
            <Card className="p-5 lg:col-span-2" data-testid="hq-client-nba-card">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Next best action</div>
                  <div className="mt-2 flex items-center gap-3">
                    <NudgeChip nudge={data.next_best_action} compact={false} testId="hq-client-nba-chip"/>
                  </div>
                  <p className="mt-2 text-sm text-[#4A4A4A]" data-testid="hq-client-nba-reason">
                    {data.next_best_action.reason}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="hq-client-nba-take-action"
                  onClick={() => {
                    const nba = data.next_best_action;
                    const t = (nba?.type || "").toUpperCase();
                    if (t === "PITCH_WEBSITE") { setPitchType("website"); return; }
                    if (t === "OFFER_OPTIMISE") { setPitchType("optimise"); return; }
                    const map = { invoices: "invoices", leads: "leads", subs: "subs",
                                  services: "services", preview: "overview",
                                  rescan: "overview" };
                    setTab(map[nba?.action] || "overview");
                  }}
                  className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00]"
                >
                  Take action
                </button>
              </div>
            </Card>
          )}
          <Card className="p-6">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Business</div>
            <div className="mt-3">
              <Row label="Name" value={b.name}/>
              <Row label="Industry" value={b.industry}/>
              <Row label="Status" value={b.status}/>
              <Row label="Country" value={b.country || b.address}/>
              <Row
                label="Territory"
                value={
                  <span data-testid="hq-client-profile-territory"
                        className="inline-flex items-center gap-2">
                    {b.territory ? (
                      <>
                        <span aria-hidden className="text-lg leading-none">
                          {(TERRITORY_META[b.territory] || {}).flag || territoryFlag(b.territory)}
                        </span>
                        <span className="font-black tracking-[0.06em]">
                          {territoryLabel(b.territory)}
                        </span>
                      </>
                    ) : (
                      <span className="text-[#8A8A8A]">NOT SET</span>
                    )}
                  </span>
                }
              />
            </div>
          </Card>
          <Card className="p-6">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Primary contact</div>
            <div className="mt-3">
              <Row label="Name" value={contact.full_name}/>
              <Row label="Email" value={contact.email}/>
              <Row label="Phone" value={
                <PhoneDisplay value={contact.phone || b.phone || ""}
                              testId="hq-client-profile-phone"/>
              }/>
              <Row label="Website" value={web && (
                <a href={web.url || `https://${web.normalized_domain}`} target="_blank" rel="noreferrer"
                  className="text-[#FF6A00] hover:underline inline-flex items-center gap-1">
                  {web.normalized_domain || web.url} <ExternalLink size={11}/>
                </a>
              )}/>
            </div>
          </Card>
          {(() => {
            const cs = data.commercial_summary || {};
            const obc = cs.outstanding_by_currency || {};
            const currencies = Object.keys(obc);
            const fmt = (m, cur) => {
              const sym = { GBP: "£", USD: "$", EUR: "€", AED: "AED " }[cur] || cur + " ";
              return `${sym}${(Number(m || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };
            return (
              <Card className="p-6 lg:col-span-2" data-testid="hq-client-commercial-summary">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Commercial snapshot</div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-xl border border-[#EAEAEA] px-3 py-3" data-testid="hq-client-outstanding-balance">
                    <div className="text-[9px] tracking-[0.20em] uppercase font-black text-[#8A8A8A]">Outstanding balance</div>
                    {currencies.length === 0 ? (
                      <div className="mt-1 font-black text-2xl text-[#0FA958]">£0.00</div>
                    ) : (
                      <div className="mt-1 space-y-0.5">
                        {currencies.map((cur) => (
                          <div key={cur} className="font-black text-lg text-[#8A4A00]"
                               data-testid={`hq-client-outstanding-${cur}`}>
                            {fmt(obc[cur].amount_minor, cur)}
                            <span className="ml-1 text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">{cur} · {obc[cur].count} inv</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl border border-[#EAEAEA] px-3 py-3">
                    <div className="text-[9px] tracking-[0.20em] uppercase font-black text-[#8A8A8A]">Active services</div>
                    <div className="mt-1 font-black text-2xl" data-testid="hq-client-active-services">{cs.active_services_count || 0}</div>
                  </div>
                  <div className="rounded-xl border border-[#EAEAEA] px-3 py-3">
                    <div className="text-[9px] tracking-[0.20em] uppercase font-black text-[#8A8A8A]">Active subscriptions</div>
                    <div className="mt-1 font-black text-2xl" data-testid="hq-client-active-subs">{cs.active_subscriptions_count || 0}</div>
                  </div>
                  <div className="rounded-xl border border-[#EAEAEA] px-3 py-3">
                    <div className="text-[9px] tracking-[0.20em] uppercase font-black text-[#8A8A8A]">Open invoices</div>
                    <div className="mt-1 font-black text-2xl" data-testid="hq-client-open-invoices">{cs.open_invoice_count || 0}</div>
                  </div>
                </div>
              </Card>
            );
          })()}
        </div>
      )}

      {tab === "services" && (
        <ClientServicesTab
          businessId={id}
          slots={data.service_slots || []}
          onChange={(items) => setData((prev) => prev ? { ...prev, service_slots: items } : prev)}
        />
      )}

      {tab === "pitch" && (
        <ClientPitchTab
          businessId={id}
          serviceSlots={data.service_slots || []}
          orangeScore={data.orange_score_current}
          onPitchClick={(type) => setPitchType(type)}
        />
      )}

      {tab === "subs" && (
        <ClientSubscriptionsTab
          businessId={id}
          primaryContact={contact}
          primaryEmail={contact.email || b.email}
          businessName={b.name}
        />
      )}

      {tab === "invoices" && (
        <ClientInvoicesTab
          businessId={id}
          contacts={data.contacts || (contact.id ? [contact] : [])}
          primaryEmail={contact.email || b.email}
          businessName={b.name}
          serviceSlots={data.service_slots || []}
        />
      )}

      {tab === "fix" && (
        <ClientOrangeFixTab businessId={id}/>
      )}

      {tab === "agreements" && (
        <ClientAgreementsTab businessId={id}/>
      )}

      {tab === "comms" && (
        <ClientCommsTimeline
          communications={data.communications || []}
          businessId={id}
          businessName={b.name}
          primaryContact={contact}
          primaryEmail={contact.email || b.email}
          onFollowupSent={refresh}
        />
      )}

      {tab === "activity" && (
        <div className="space-y-6">
          <ClientPitchHistory businessId={data.business?.id || data.business_id}/>
          <ClientActivityTimeline activities={data.activities || []}/>
        </div>
      )}

      <ClientEmailModal
        open={introOpen}
        mode="intro"
        businessId={id}
        onClose={() => setIntroOpen(false)}
        onSent={refresh}
      />
      <ClientEmailModal
        open={previewOpen}
        mode="preview"
        businessId={id}
        onClose={() => setPreviewOpen(false)}
        onSent={refresh}
      />
      <SalesPitchModal
        open={!!pitchType}
        pitchType={pitchType}
        businessId={id}
        onClose={() => setPitchType(null)}
        onSent={refresh}
      />
    </div>
  );
}
